/**
 * GraphQL error normalization for the rewards API.
 *
 * The backend (`GraphqlExceptionHandler`, Ticketon.Rewards README §4) puts:
 * - `extensions.code`: the HTTP status name (e.g. `"UnprocessableEntity"`),
 * - `extensions.errorCode`: the `TicketonErrorCodes` NAME (e.g. `"InsufficientRewardsPoints"`),
 * - `extensions.correlationId`: the request trace id,
 * and a `message` that is already localized.
 *
 * The error is inspected structurally instead of through Apollo's error classes,
 * so both the Apollo v3 shape (`ApolloError.graphQLErrors` / `networkError`) and
 * the v4 shape (`CombinedGraphQLErrors.errors`, `ServerError`) are understood.
 *
 * UIs must not render `message` directly: map the error to a translation key
 * with {@link rewardsErrorMessageKey}.
 */

/** Rewards error names from Ticketon.Rewards README §4 (the `TicketonErrorCodes` names). */
export const REWARDS_SERVER_ERROR_CODES = [
  "RewardsProgramDoesNotExist",
  "InvalidRewardsProgram",
  "RewardsProgramCodeAlreadyExists",
  "RewardsProgramNotActive",
  "RewardsTierDoesNotExist",
  "InvalidRewardsTier",
  "RewardsTierRankAlreadyExists",
  "RewardsEarningRuleDoesNotExist",
  "InvalidRewardsEarningRule",
  "RewardsCatalogItemDoesNotExist",
  "InvalidRewardsCatalogItem",
  "RewardsCatalogItemNotRedeemable",
  "RewardsCatalogItemOutOfStock",
  "RewardsRedemptionLimitReached",
  "InsufficientRewardsPoints",
  "RewardsRedemptionDoesNotExist",
  "RewardsRedemptionStatusTransitionNotAllowed",
  "RewardsMemberDoesNotExist",
  "InvalidRewardsPointAdjustment",
  "RewardsPromoterNotFound",
  "InvalidRewardsPagination",
  "InvalidRewardsSimulation",
  "InvalidRewardsSearch",
] as const;

/**
 * A rewards error code: one of the README §4 names, `InvalidToken` (the caller
 * is not authenticated / the token expired), `Unauthorized` (authenticated but
 * lacking the permission) or `Unknown` (anything else, including network
 * failures — check `isNetworkError`).
 */
export type RewardsErrorCode =
  | (typeof REWARDS_SERVER_ERROR_CODES)[number]
  | "InvalidToken"
  | "Unauthorized"
  | "Unknown";

/** Every {@link RewardsErrorCode}. */
export const REWARDS_ERROR_CODES: readonly RewardsErrorCode[] = [
  ...REWARDS_SERVER_ERROR_CODES,
  "InvalidToken",
  "Unauthorized",
  "Unknown",
];

/** A translation key produced by {@link rewardsErrorMessageKey}. */
export type RewardsErrorMessageKey = `rewards.error.${string}`;

/** The stable error shape surfaced by every rewards hook. */
export interface INormalizedError {
  /**
   * Server-provided message (already localized by the backend) or a fallback
   * token such as `"network_error"`. For logging only — render
   * {@link rewardsErrorMessageKey} instead.
   */
  message: string;
  /** `extensions.code`: the HTTP status name, e.g. `"UnprocessableEntity"`. */
  code?: string;
  /** The rewards error code resolved from `extensions.errorCode`. */
  errorCode: RewardsErrorCode;
  /** The raw `extensions.errorCode` sent by the server, even when not a known rewards code. */
  serverErrorCode?: string;
  /** `extensions.correlationId`, for support tickets and log correlation. */
  correlationId?: string;
  /** Per-field validation messages keyed by form field path. */
  fieldErrors?: Record<string, string>;
  /** True when the failure was a transport/network error. */
  isNetworkError: boolean;
  /** The original error, for logging. */
  raw?: unknown;
}

interface GraphQLErrorLike {
  message?: string;
  extensions?: {
    code?: unknown;
    errorCode?: unknown;
    correlationId?: unknown;
    fieldErrors?: Record<string, string>;
    validation?: Record<string, string>;
  };
}

const KNOWN_ERROR_CODES = new Set<string>(REWARDS_ERROR_CODES);

/** `extensions.code` values (HTTP status names + HotChocolate auth codes) meaning "not authenticated". */
const UNAUTHENTICATED_CODES = new Set(["Unauthorized", "AUTH_NOT_AUTHENTICATED"]);

/** `extensions.code` values meaning "authenticated but not allowed". */
const FORBIDDEN_CODES = new Set(["Forbidden", "AUTH_NOT_AUTHORIZED"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** Whether the value is already an {@link INormalizedError}. */
export function isNormalizedError(value: unknown): value is INormalizedError {
  return (
    isRecord(value) &&
    typeof value["message"] === "string" &&
    typeof value["errorCode"] === "string" &&
    typeof value["isNetworkError"] === "boolean"
  );
}

/** Builds a normalized error that did not come from the server (e.g. an empty response). */
export function createNormalizedError(
  errorCode: RewardsErrorCode,
  message: string,
  raw?: unknown,
): INormalizedError {
  const error: INormalizedError = { message, errorCode, isNetworkError: false };
  if (raw !== undefined) error.raw = raw;
  return error;
}

/** Extracts the GraphQL errors from any Apollo v3/v4 error shape. */
function extractGraphQLErrors(error: unknown): GraphQLErrorLike[] {
  if (!isRecord(error)) return [];
  // Apollo v3: error.graphQLErrors. Apollo v4 CombinedGraphQLErrors: error.errors.
  const candidates = error["graphQLErrors"] ?? error["errors"];
  if (Array.isArray(candidates)) {
    return candidates.filter(isRecord) as GraphQLErrorLike[];
  }
  return [];
}

/** The transport error, if any: v3 nests it under `networkError`, v4 throws it directly. */
function extractNetworkError(error: unknown): Record<string, unknown> | null {
  if (!isRecord(error)) return null;
  if (isRecord(error["networkError"])) return error["networkError"];
  const name = readString(error["name"]);
  if (name === "ServerError" || name === "ServerParseError" || name === "TypeError") {
    return error;
  }
  return null;
}

function errorCodeFromHttpStatus(statusCode: unknown): RewardsErrorCode | undefined {
  if (statusCode === 401) return "InvalidToken";
  if (statusCode === 403) return "Unauthorized";
  return undefined;
}

function resolveErrorCode(
  serverErrorCode: string | undefined,
  code: string | undefined,
  networkError: Record<string, unknown> | null,
): RewardsErrorCode {
  if (serverErrorCode && KNOWN_ERROR_CODES.has(serverErrorCode)) {
    return serverErrorCode as RewardsErrorCode;
  }
  if (code && UNAUTHENTICATED_CODES.has(code)) return "InvalidToken";
  if (code && FORBIDDEN_CODES.has(code)) return "Unauthorized";
  return errorCodeFromHttpStatus(networkError?.["statusCode"]) ?? "Unknown";
}

/**
 * Normalizes any thrown/returned GraphQL error into {@link INormalizedError}.
 * Never throws. An already-normalized error is returned unchanged.
 */
export function normalizeGraphQLError(error: unknown): INormalizedError {
  if (isNormalizedError(error)) return error;
  if (!error) return createNormalizedError("Unknown", "unknown_error");

  const networkError = extractNetworkError(error);
  const isNetworkError = networkError !== null;
  const gqlErrors = extractGraphQLErrors(error);
  const first = gqlErrors[0];

  const fieldErrors: Record<string, string> = {};
  for (const gqlError of gqlErrors) {
    const fromExtensions = gqlError.extensions?.fieldErrors ?? gqlError.extensions?.validation;
    if (fromExtensions) Object.assign(fieldErrors, fromExtensions);
  }

  const code = readString(first?.extensions?.code);
  const serverErrorCode = readString(first?.extensions?.errorCode);
  const correlationId = readString(first?.extensions?.correlationId);

  const message =
    readString(first?.message) ??
    (isNetworkError ? "network_error" : undefined) ??
    (isRecord(error) ? readString(error["message"]) : undefined) ??
    "unknown_error";

  const result: INormalizedError = {
    message,
    errorCode: resolveErrorCode(serverErrorCode, code, networkError),
    isNetworkError,
    raw: error,
  };
  if (code) result.code = code;
  if (serverErrorCode) result.serverErrorCode = serverErrorCode;
  if (correlationId) result.correlationId = correlationId;
  if (Object.keys(fieldErrors).length > 0) result.fieldErrors = fieldErrors;
  return result;
}

function lowerFirst(value: string): string {
  return value.charAt(0).toLowerCase() + value.slice(1);
}

/**
 * Translation key for an error, e.g. `"rewards.error.insufficientRewardsPoints"`.
 * Network failures map to `"rewards.error.network"`; anything unrecognized to
 * `"rewards.error.unknown"`. Accepts a raw Apollo error or a normalized one.
 */
export function rewardsErrorMessageKey(error: unknown): RewardsErrorMessageKey {
  const normalized = normalizeGraphQLError(error);
  if (normalized.isNetworkError && normalized.errorCode === "Unknown") {
    return "rewards.error.network";
  }
  return `rewards.error.${lowerFirst(normalized.errorCode)}`;
}
